import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import { 
  Layout, 
  Card, 
  Avatar, 
  Typography, 
  Space, 
  Table, 
  Tag, 
  Button, 
  Row, 
  Col, 
  List,
  Statistic,
  Badge,
  Segmented,
  Tooltip
} from 'antd';
import { 
  MailOutlined, 
  PhoneOutlined, 
  HomeOutlined, 
  UserOutlined, 
  CalendarOutlined,
  BookOutlined,
  InfoCircleOutlined,
  EnvironmentOutlined,
  QuestionCircleOutlined,
  VideoCameraOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  StarOutlined,
  TrophyOutlined,
  FileTextOutlined,
  ArrowRightOutlined,
  HistoryOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import getUserIdFromToken from '../../Utils/authUtils';
import Modal from '../../components/Modal';
import moment from 'moment';
import thLocale from '@fullcalendar/core/locales/th';
import './Student.css';
import { useTranslation } from 'react-i18next';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const { Content } = Layout;
const { Title, Text } = Typography;

function StudentDashboard() {
  const { t, i18n } = useTranslation();
  const userId = getUserIdFromToken();
  const [student, setStudent] = useState(null);
  const [studentInfo, setStudentInfo] = useState([]);
  const [studentTestRegistrations, setStudentTestRegistrations] = useState([]);
  const [error, setError] = useState(null);
  const [studentAttendance, setStudentAttendance] = useState([]);
  const [billingHistory, setBillingHistory] = useState([]);
  const [popupData, setPopupData] = useState(null);
  const [classChangeLogs, setClassChangeLogs] = useState([]);
  const [absentCounts, setAbsentCounts] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [scheduleView, setScheduleView] = useState('upcoming');
  const itemsPerPage = 10;

  const API_URL = process.env.REACT_APP_API_URL;

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
  };

  const platformMap = {
    athome: {
      label: t('athome'),
      icon: <HomeOutlined />,
      color: 'orange',
    },
    online: {
      label: t('online'),
      icon: <VideoCameraOutlined />,
      color: 'green',
    },
    onsite: {
      label: t('onsite'),
      icon: <EnvironmentOutlined />,
      color: 'blue',
    },
  };

  const getDayOfWeek = (dateString) => {
    try {
      const date = new Date(dateString);
      const dayIndex = date.getDay();
      const daysOfWeek = t('daysOfWeek', { returnObjects: true });
      return daysOfWeek[dayIndex];
    } catch (error) {
      console.error('Invalid date value:', dateString);
      return t('invalidDay');
    }
  };

  useEffect(() => {
    const fetchStudentData = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/students/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const studentData = response.data;
        
        let photoUrl = 'https://via.placeholder.com/150';
        
        if (studentData.profile_photo) {
          const baseUrl = API_URL.replace(/\/api.*$/, '').replace(/\/$/, '');
          if (studentData.profile_photo.startsWith('/')) {
            photoUrl = `${baseUrl}${studentData.profile_photo}`;
          } else if (studentData.profile_photo.startsWith('http')) {
            photoUrl = studentData.profile_photo;
          }
        }

        setStudent({
          ...studentData,
          photoUrl: photoUrl,
        });
      } catch (error) {
        console.error('Error fetching student data:', error);
        setError('Failed to fetch student data.');
      }
    };

    const fetchStudentAttendance = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/attendance/student/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStudentAttendance(response.data);
        calculateAbsences(response.data);
      } catch (error) {
        console.error('Error fetching student attendance:', error);
      }
    };

    const calculateAbsences = (attendanceData) => {
      const absences = {};
      attendanceData.forEach(attendance => {
        const statusLowerCase = attendance.status.toLowerCase();
        if (statusLowerCase === 'absent') {
          if (!absences[attendance.class_code]) {
            absences[attendance.class_code] = 1;
          } else {
            absences[attendance.class_code] += 1;
          }
        }
      });
      setAbsentCounts(absences);
    };

    const fetchBillingHistory = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/registrations/user`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const { registrations } = response.data;

        const sortedRegistrations = registrations.map(reg => {
          const sortedSchedule = reg.schedule.sort((a, b) => {
            const dateTimeA = moment(`${a.date} ${a.time}`, 'YYYY-MM-DD HH:mm:ss');
            const dateTimeB = moment(`${b.date} ${b.time}`, 'YYYY-MM-DD HH:mm:ss');
            return dateTimeA - dateTimeB;
          });
          return { ...reg, schedule: sortedSchedule };
        });

        sortedRegistrations.sort((a, b) => {
          const earliestA = a.schedule[0] ? moment(`${a.schedule[0].date} ${a.schedule[0].time}`, 'YYYY-MM-DD HH:mm:ss') : moment(0);
          const earliestB = b.schedule[0] ? moment(`${b.schedule[0].date} ${b.schedule[0].time}`, 'YYYY-MM-DD HH:mm:ss') : moment(0);
          return earliestA - earliestB;
        });

        setBillingHistory(sortedRegistrations);
      } catch (error) {
        console.error('Error fetching billing history:', error);
      }
    };

    const fetchStudentTestRegistrations = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/test-scores/test-registrations`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStudentTestRegistrations(response.data);
      } catch (error) {
        console.error('Error fetching student test registrations:', error);
      }
    };

    const fetchStudentInfo = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/student-info/student/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStudentInfo(response.data);
      } catch (error) {
        console.error('Error fetching student info:', error);
      }
    };

    fetchStudentData();
    fetchStudentAttendance();
    fetchBillingHistory();
    fetchStudentTestRegistrations(); 
    fetchStudentInfo();
  }, [userId, API_URL]);

  useEffect(() => {
    const fetchClassChangeLogs = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/class-change/class-change-logs`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setClassChangeLogs(response.data);
      } catch (error) {
        console.error('Error fetching class change logs:', error);
      }
    };
    fetchClassChangeLogs();
  }, [API_URL]);
 
  const calculateEndTime = (startTime, duration) => {
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const [durationHours, durationMinutes] = duration.split(':').map(Number);

    let endHours = startHours + durationHours;
    let endMinutes = startMinutes + durationMinutes;

    if (endMinutes >= 60) {
      endHours += Math.floor(endMinutes / 60);
      endMinutes %= 60;
    }

    return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
  };

  const formatDate = useCallback((dateString) => {
    const date = new Date(dateString);
    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    return new Intl.DateTimeFormat(i18n.language, options).format(date).toUpperCase();
  }, [i18n.language]);

  // Calculate statistics
  const stats = useMemo(() => {
    const allClasses = billingHistory.flatMap(enrollment => enrollment.schedule);
    const now = moment();
    const upcomingClasses = allClasses.filter(cls => moment(`${cls.date} ${cls.time}`, 'YYYY-MM-DD HH:mm:ss').isAfter(now));
    const pastClasses = allClasses.filter(cls => moment(`${cls.date} ${cls.time}`, 'YYYY-MM-DD HH:mm:ss').isBefore(now));
    
    const totalClasses = allClasses.length;
    const attendedClasses = studentAttendance.filter(log => log.status === 'Present').length;
    const totalHours = billingHistory.reduce((sum, enrollment) => sum + parseFloat(enrollment.total_hours || 0), 0);
    const averageHomework = studentAttendance.filter(att => att.homework === 'YES').length;

    return {
      totalClasses,
      upcomingClasses: upcomingClasses.length,
      pastClasses: pastClasses.length,
      attendedClasses,
      totalHours,
      averageHomework,
      attendanceRate: totalClasses > 0 ? ((attendedClasses / totalClasses) * 100).toFixed(1) : 0,
      homeworkRate: studentAttendance.length > 0 ? ((averageHomework / studentAttendance.length) * 100).toFixed(1) : 0
    };
  }, [billingHistory, studentAttendance]);

  // Separate upcoming and past classes
  const { upcomingSchedule, pastSchedule } = useMemo(() => {
    const allSchedule = billingHistory
      .flatMap(enrollment =>
        enrollment.schedule.map(schedule => ({
          ...schedule,
          class_code: enrollment.class_code,
          platform: enrollment.platform
        }))
      );
    
    const now = moment();
    const upcoming = allSchedule
      .filter(cls => moment(`${cls.date} ${cls.time}`, 'YYYY-MM-DD HH:mm:ss').isAfter(now))
      .sort((a, b) => moment(`${a.date} ${a.time}`, 'YYYY-MM-DD HH:mm:ss') - moment(`${b.date} ${b.time}`, 'YYYY-MM-DD HH:mm:ss'));
    
    const past = allSchedule
      .filter(cls => moment(`${cls.date} ${cls.time}`, 'YYYY-MM-DD HH:mm:ss').isBefore(now))
      .sort((a, b) => moment(`${b.date} ${b.time}`, 'YYYY-MM-DD HH:mm:ss') - moment(`${a.date} ${a.time}`, 'YYYY-MM-DD HH:mm:ss'));

    return { upcomingSchedule: upcoming, pastSchedule: past };
  }, [billingHistory]);

  const currentScheduleData = scheduleView === 'upcoming' ? upcomingSchedule : pastSchedule;
  const paginatedSchedule = useMemo(() => {
    return currentScheduleData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  }, [currentScheduleData, currentPage, itemsPerPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleScheduleViewChange = (view) => {
    setScheduleView(view);
    setCurrentPage(1);
  };

  // Toast notifications for absences and test reminders
  useEffect(() => {
    const recentDate = new Date();
    recentDate.setDate(recentDate.getDate() - 7);
  
    const getDismissedToasts = () => {
      const dismissed = localStorage.getItem('dismissedToasts');
      return dismissed ? JSON.parse(dismissed) : [];
    };
  
    const saveDismissedToasts = (dismissedToasts) => {
      localStorage.setItem('dismissedToasts', JSON.stringify(dismissedToasts));
    };
  
    const dismissedToasts = getDismissedToasts();
  
    studentAttendance.forEach(attendance => {
      const attendanceDate = new Date(attendance.timestamp);
  
      if (attendanceDate >= recentDate && attendance.status.toLowerCase() === 'absent') {
        const toastId = `${attendance.class_code}_${attendance.timestamp}`;
        
        if (!dismissedToasts.includes(toastId)) {
          const count = absentCounts[attendance.class_code] || 1;
  
          toast.info(
            `You were marked absent for class ${attendance.class_code} on ${formatDate(attendance.timestamp)}. This is your ${count}${getOrdinalSuffix(count)} absence for this class.`,
            {
              position: "top-right",
              autoClose: true,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              className: 'absent-toast',
              style: {
                backgroundColor: '#f8d7da',
                color: '#721c24',
                border: '1px solid #f5c6cb',
              },
              onClose: () => {
                const updatedDismissedToasts = [...dismissedToasts, toastId];
                saveDismissedToasts(updatedDismissedToasts);
              },
            }
          );
        }
      }
    });
  }, [studentAttendance, absentCounts, formatDate]);
  
  useEffect(() => {
    const today = new Date();
  
    studentTestRegistrations.forEach(test => {
      const testDate = new Date(test.test_date);
      const daysBeforeTest = Math.ceil((testDate - today) / (1000 * 60 * 60 * 24));
  
      if (daysBeforeTest === 1) {
        toast.info(
          `Reminder: Your ${test.test_type} (${test.sub_type}) test is scheduled on ${formatDate(test.test_date)}.`,
          {
            position: "top-right",
            autoClose: true,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            style: {
              backgroundColor: '#f8d7da',
              border: '1px solid red',
              color: 'black',
            },
            icon: '⚠️',
          }
        );
      }
    });
  }, [studentTestRegistrations, formatDate]);

const handlePrintPDF = async () => {
  const calendarElement = document.getElementById('calendar-container');
  if (!calendarElement || !student) return;

  const canvas = await html2canvas(calendarElement, { scale: 2 });
  const imgData = canvas.toDataURL('image/png');

  const pdf = new jsPDF('landscape', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15; // Increased margin for better spacing

  // Header background (optional - light gray bar)
  pdf.setFillColor(248, 249, 250);
  pdf.rect(0, 0, pageWidth, 35, 'F');

  // Title section
  let yOffset = 12;
  pdf.setFont('Helvetica', 'bold');
  pdf.setFontSize(20);
  pdf.setTextColor(33, 37, 41); // Dark gray
  pdf.text(`${student.firstname} ${student.lastname}`, margin, yOffset);

  // Add current date to the right
  pdf.setFont('Helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(108, 117, 125); // Medium gray
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  const dateText = `Generated: ${currentDate}`;
  const dateWidth = pdf.getTextWidth(dateText);
  pdf.text(dateText, pageWidth - margin - dateWidth, yOffset);

  // Student info section
  yOffset += 8;
  pdf.setFont('Helvetica', 'normal');
  pdf.setFontSize(11);

  // Create info badges/tags with better styling
  const drawInfoBadge = (text, x, y, color, bgColor = null) => {
    pdf.setTextColor(...color);
    if (bgColor) {
      const textWidth = pdf.getTextWidth(text);
      pdf.setFillColor(...bgColor);
      pdf.roundedRect(x - 2, y - 3, textWidth + 4, 5, 1, 1, 'F');
    }
    pdf.text(text, x, y);
  };

  // Contact information section
  yOffset += 1;
  pdf.setTextColor(73, 80, 87); // Dark gray
  pdf.setFont('Helvetica', 'normal');
  pdf.setFontSize(10);

  const contactInfo = [];
  if (student.email) contactInfo.push(`${student.email}`);
  if (student.phone) contactInfo.push(`${student.phone}`);
  
  if (contactInfo.length > 0) {
    pdf.text(contactInfo.join('  •  '), margin, yOffset);
  }

  // Add a subtle separator line
  yOffset += 6;
  pdf.setDrawColor(222, 226, 230);
  pdf.setLineWidth(0.5);
  pdf.line(margin, yOffset, pageWidth - margin, yOffset);

  // Calendar title
  yOffset += 8;
  pdf.setFont('Helvetica', 'bold');
  pdf.setFontSize(14);
  pdf.setTextColor(33, 37, 41);
  pdf.text('Academic Calendar', margin, yOffset);

  // Calendar image area
  yOffset += 8;

  const availableWidth = pageWidth - margin * 2;
  const availableHeight = pageHeight - yOffset - margin;

  const imgProps = pdf.getImageProperties(imgData);
  const ratio = Math.min(availableWidth / imgProps.width, availableHeight / imgProps.height);
  const imgWidth = imgProps.width * ratio;
  const imgHeight = imgProps.height * ratio;

  const x = (pageWidth - imgWidth) / 2;
  const y = yOffset;

  // Add subtle border around calendar
  pdf.setDrawColor(222, 226, 230);
  pdf.setLineWidth(0.3);
  pdf.rect(x - 1, y - 1, imgWidth + 2, imgHeight + 2);

  pdf.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight);

  // Footer (optional)
  pdf.setFont('Helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(108, 117, 125);
  const footerText = 'This calendar is generated automatically and subject to updates.';
  const footerWidth = pdf.getTextWidth(footerText);
  pdf.text(footerText, (pageWidth - footerWidth) / 2, pageHeight - 5);

  pdf.save(`${student.firstname || 'student'}-calendar-${new Date().toISOString().split('T')[0]}.pdf`);
};

const getChangeReasonDisplay = (reason) => {
  switch (reason) {
    case "admin_adjust":
      return { text: "Admin", color: "#678d5b" };
    case "cancelled_by_student":
      return { text: "CL by S.", color: "red" };
    case "cancelled_by_teacher":
      return { text: "CL by T.", color: "orange" };
    case "class_on_hold_by_student":
      return { text: "On hold by S.", color: "blue" };
    case "class_on_hold_by_school":
      return { text: "On hold by P.", color: "green" };
    case "cancelled_same_day_teacher":
      return { text: "CSD (Teacher)", color: "#FF4500" };
    case "cancelled_same_day_student":
      return { text: "CSD (Student)", color: "#FF4500" };
    case "teacher_late":
      return { text: "Teacher Late", color: "gold" };
    case "kease_cut":
      return { text: "KEAES Cut", color: "magenta" };
    case "make_up":
      return { text: "Make Up", color: "#8A2BE2" };
    case "others":
      return { text: "Others", color: "darkgrey" };
    default:
      return { text: reason || "", color: "black" };
  }
};

  
  const getOrdinalSuffix = (i) => {
    const j = i % 10,
      k = i % 100;
    if (j === 1 && k !== 11) {
      return "st";
    }
    if (j === 2 && k !== 12) {
      return "nd";
    }
    if (j === 3 && k !== 13) {
      return "rd";
    }
    return "th";
  };

  const closePopup = () => {
    setPopupData(null);
  };

  const eventContent = (eventInfo) => {
    const { subject, teacher, classroom } = eventInfo.event.extendedProps;
    const startTime = eventInfo.event.startStr.split('T')[1].slice(0, 5);
    const endTime = eventInfo.event.endStr.split('T')[1].slice(0, 5);

    return (
      <div style={{ padding: '4px', fontSize: '11px', lineHeight: '1.2' }}>
        <div style={{ marginBottom: '2px' }}>{`${startTime} - ${endTime}`}</div>
        <div style={{ marginBottom: '1px' }}>{eventInfo.event.title}</div>
        <div style={{ marginBottom: '1px' }}>{subject}</div>
        <div style={{ marginBottom: '1px' }}>{teacher}</div>
        {classroom && <div>{`Room: ${classroom}`}</div>}
      </div>
    );
  };

  // Mobile-friendly table columns
  const scheduleColumns = [
    {
      title: t('date'),
      dataIndex: 'date',
      key: 'date',
      render: (date) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{formatDate(date)}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {moment(date).format('dddd')}
          </div>
        </div>
      ),
      width: 120,
    },
    { title: t('classId'), dataIndex: 'classid' },
    {
      title: t('class'),
      key: 'class_info',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{record.class_code}</div>
          <div style={{ fontSize: '12px' }}>{record.subject}</div>
        </div>
      ),
      width: 250,
    },
    { title: t('teacher'), dataIndex: 'teacher' },
    {
      title: t('time'),
      key: 'time_info',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>
            {`${formatTime(record.time)} - ${calculateEndTime(record.time, record.hours)}`}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>{record.hours} {t('hr')}</div>
        </div>
      ),
      width: 120,
    },
    
    {
                            title: t('platform'),
                            dataIndex: 'platform',
                            render: (platform) => {
                              const config = platformMap[platform];
                              
                              if (!config) {
                                return (
                                  <Tag color="default" icon={<QuestionCircleOutlined />}>
                                    {platform || '-'}
                                  </Tag>
                                );
                              }

                              return (
                                <Tag color={config.color} icon={config.icon}>
                                  {config.label}
                                </Tag>
                              );
                            }
                          },
                          {
                              title: t('attendance'),
                              render: (_, cls) => {
                                const attendance = studentAttendance.find((att) => att.classid === cls.classid);
                                const status = attendance ? attendance.status : '';

                                const statusMap = {
                                  Present: { label: t('present'), color: 'green' },
                                  Absent: { label: t('absent'), color: 'red' },
                                  Late_15: { label: t('Late ≤ 15 M'), color: 'orange' },
                                  Late_Over15: { label: t('Late > 15 M'), color: 'volcano' }
                                };

                                return status ? (
                                  <Tag color={statusMap[status]?.color || 'default'}>
                                    {statusMap[status]?.label || status}
                                  </Tag>
                                ) : (
                                  <Tag>-</Tag>
                                );
                              },
                            },
                          {
                            title: t('homework'),
                            render: (_, cls) => {
                              const attendance = studentAttendance.find((att) => att.classid === cls.classid);
                              const homework = attendance ? attendance.homework : 'N/A';

                              const homeworkStyles = {
                                YES: {
                                  color: '#389e0d',
                                  backgroundColor: '#f6ffed',
                                  border: '1px solid #b7eb8f',
                                },
                                NO: {
                                  color: '#cf1322',
                                  backgroundColor: '#fff1f0',
                                  border: '1px solid #ffa39e',
                                },
                                'N/A': {
                                  color: '#8c8c8c',
                                  backgroundColor: '#f5f5f5',
                                  border: '1px solid #d9d9d9',
                                },
                                
                              };

                              return (
                                <Tag
                                  style={{
                                    ...homeworkStyles[homework] || homeworkStyles['N/A'],
                                    borderRadius: '12px',
                                    padding: '2px 12px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                  }}
                                >
                                  {homework}
                                </Tag>
                              );
                            },
                          },
                                                    {
                                title: t('classchange'),
                                key: 'changereason',
                                render: (text, record) => {
                                  const log = classChangeLogs.find(log => log.classid === record.classid);
                                  const { text: changeReasonText, color } = getChangeReasonDisplay(log?.changereason);
                                  return <Tag color={color}>{changeReasonText}</Tag>;
                                }
                              },
                              {
                                title: t('note'),
                                key: 'note',
                                render: (_, record) => {
                                  const log = classChangeLogs.find(log => log.classid === record.classid);
                                  return log?.note ? <span>{log.note}</span> : <span style={{ color: '#aaa' }}>{t('No Note')}</span>;
                                }
                              },  
  ];

  if (error) return <p>{error}</p>;
  if (!student) return <p>Loading...</p>;

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sidebar />
      <Layout>
        <Header />
        <ToastContainer />
        <Content style={{ padding: '12px 16px', background: '#f5f5f5' }}>
          {/* Student Header - Compact & Responsive */}
          <Card 
            style={{ 
              marginBottom: '16px',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
            }}
          >
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={8} md={6}>
                <div style={{ textAlign: 'center' }}>
                  <Avatar 
                    size={80} 
                    src={student.photoUrl} 
                    icon={<UserOutlined />}
                    style={{
                      border: '3px solid #fff',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      marginBottom: '8px'
                    }}
                  />
                </div>
              </Col>
              
              <Col xs={24} sm={16} md={12}>
                <div>
                  <Title level={3} style={{ margin: '0 0 8px 0', fontSize: '1.5rem' }}>
                    {student?.firstname} {student?.lastname}
                  </Title>
                  <Space wrap size="small">
                    <Tag color="blue">{student?.schoolid}</Tag>
                    <Tag color="green">{student?.currenteducationlevel}</Tag>
                  </Space>
                  <div style={{ marginTop: '8px' }}>
                    <Space wrap size="small">
                      <Space size="small">
                        <MailOutlined style={{ color: '#1890ff' }} />
                        <Text type="secondary" style={{ fontSize: '12px' }}>{student?.email}</Text>
                      </Space>
                      <Space size="small">
                        <PhoneOutlined style={{ color: '#52c41a' }} />
                        <Text type="secondary" style={{ fontSize: '12px' }}>{student?.phone}</Text>
                      </Space>
                    </Space>
                  </div>
                </div>
              </Col>
              
              <Col xs={24} sm={24} md={6}>
                <Row gutter={[8, 8]}>
                  <Col xs={12} sm={6} md={12}>
                    <Statistic
                      title={t('Total Classes')}
                      value={stats.totalClasses}
                      prefix={<CalendarOutlined />}
                      valueStyle={{ fontSize: '16px', color: '#1890ff' }}
                    />
                  </Col>
                  <Col xs={12} sm={6} md={12}>
                    <Statistic
                      title={t('Upcoming')}
                      value={stats.upcomingClasses}
                      prefix={<ArrowRightOutlined />}
                      valueStyle={{ fontSize: '16px', color: '#722ed1' }}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </Card>

 

          {/* Calendar Section */}
          <Card
              title={
                <Space>
                  <CalendarOutlined />
                  {t('calendar')}
                  <Badge count={stats.totalClasses} showZero color="#1890ff" />
                </Space>
              }
              extra={
                <Button onClick={handlePrintPDF} type="primary">
                  Download Calendar
                </Button>
              }
              style={{ marginBottom: '16px', borderRadius: '12px' }}
            >
                        
            <div id="calendar-container" style={{ background: '#fff', borderRadius: '8px' }}>

              <FullCalendar
                plugins={[dayGridPlugin, timeGridPlugin]}
                initialView="dayGridMonth"
                slotLabelFormat={{
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,  
                }}
                locale={i18n.language === 'th' ? thLocale : 'en'}
                events={billingHistory.flatMap(enrollment =>
                  enrollment.schedule.map(cls => {
                    const startTime = cls.time ? cls.time.slice(0, 5) : '00:00';
                    const startDateTime = cls.date ? `${cls.date}T${startTime}:00` : null;
                    const endTime = cls.time && cls.hours ? calculateEndTime(cls.time, cls.hours) : '00:00';
                    const endDateTime = cls.date ? `${cls.date}T${endTime}:00` : null;

                    if (!startDateTime || !endDateTime) {
                      console.warn('Missing time or date in class schedule:', cls);
                      return null;
                    }

                    return {
                      title: `${enrollment.class_code}`,
                      start: startDateTime,
                      end: endDateTime,
                      backgroundColor: moment(`${cls.date} ${cls.time}`, 'YYYY-MM-DD HH:mm:ss').isAfter(moment()) ? '#29166f' : '#8c8c8c',
                      borderColor: moment(`${cls.date} ${cls.time}`, 'YYYY-MM-DD HH:mm:ss').isAfter(moment()) ? '#29166f' : '#8c8c8c',
                      textColor: '#ffffff',
                      display: 'block',
                      extendedProps: {
                        subject: cls.subject,
                        teacher: cls.teacher,
                        classroom: cls.classroom,
                      },
                    };
                  }).filter(event => event !== null)
                )}
                eventContent={eventContent}
                headerToolbar={{
                  left: 'prev,next today',
                  center: 'title',
                  right: 'dayGridMonth,timeGridWeek',
                }}
                height="auto"
                eventDisplay="block"
                moreLinkClick="popover"
                      scrollTime="09:00:00" 
              />
            </div>
          </Card>

          

          {/* Class Schedule Section */}
          <Card 
            title={
              <Space>
                <BookOutlined />
                {t('classSchedule')}
              </Space>
            }
            extra={
              <Segmented
                value={scheduleView}
                onChange={handleScheduleViewChange}
                options={[
                  { 
                    label: (
                      <Space>
                        <ArrowRightOutlined />
                        {t('Upcoming')}
                        <Badge count={stats.upcomingClasses} color="#722ed1" />
                      </Space>
                    ), 
                    value: 'upcoming' 
                  },
                  { 
                    label: (
                      <Space>
                        <HistoryOutlined />
                        {t('Past')}
                        <Badge count={stats.pastClasses} color="#8c8c8c" />
                      </Space>
                    ), 
                    value: 'past' 
                  }
                ]}
              />
            }
            style={{ marginBottom: '16px', borderRadius: '12px' }}
          >
            <Table
              columns={scheduleColumns}
              dataSource={paginatedSchedule}
              pagination={{
                current: currentPage,
                total: currentScheduleData.length,
                pageSize: itemsPerPage,
                onChange: handlePageChange,
                showSizeChanger: false,
                showQuickJumper: false,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} classes`,
                size: 'small'
              }}
               scroll={{ x: 'max-content' }}
              style={{ overflowX: 'auto', marginTop: '20px' }}
              size="small"
              rowKey="classid"
            />
          </Card>


                   {/* Current Enrollments - Full Width */}
<Row gutter={[16, 16]}>
  <Col xs={24}>
    <Card 
      title={
        <Space>
          <TeamOutlined />
          {t('currentEnrollments')}
          <Badge count={billingHistory.length} color="#722ed1" />
        </Space>
      }
      style={{ borderRadius: '12px', marginBottom: '16px' }}
      bodyStyle={{ maxHeight: '500px', overflowY: 'auto' }}
    >
      <Row gutter={[12, 12]}>
        {billingHistory.map((enrollment, index) => (
          <Col xs={24} sm={12} md={8} lg={6} xl={4} key={index}>
            <Card 
              size="small"
              hoverable
              style={{ background: '#fafafa', height: '100%' }}
              actions={[
                <Link to={`/classdetailsstudent/${enrollment.groupid}`}>
                  <Button type="primary" size="small">
                    {t('viewDetails')}
                  </Button>
                </Link>
              ]}
            >
              <Card.Meta
                title={<Text strong>{enrollment.class_code}</Text>}
                description={
                  <Space direction="vertical" style={{ width: '100%' }} size="small">
                    <div>
                      <Text type="secondary" style={{ fontSize: '12px' }}>{t('Subjects')}:</Text>
                      <div style={{ marginTop: '4px' }}>
                        {enrollment.subjects.slice(0, 2).map((subject, idx) => (
                          <Tag key={idx} size="small" color="blue" style={{ marginBottom: '2px' }}>
                            {subject.title}
                          </Tag>
                        ))}
                        {enrollment.subjects.length > 2 && (
                          <Tag size="small" color="default">
                            +{enrollment.subjects.length - 2} more
                          </Tag>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>{t('Total Hours')}:</Text>
                      <Text strong style={{ fontSize: '12px' }}>{enrollment.total_hours} {t('hrs')}</Text>
                    </div>
                  </Space>
                }
              />
            </Card>
          </Col>
        ))}
        {billingHistory.length === 0 && (
          <Col xs={24}>
            <div style={{ 
              textAlign: 'center',
              padding: '40px',
              color: '#8c8c8c'
            }}>
              <TeamOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
              <div style={{ fontSize: '16px' }}>{t('No current enrollments')}</div>
            </div>
          </Col>
        )}
      </Row>
    </Card>
  </Col>
</Row>
          {/* Additional Info Row - Student Details */}
          <Row style={{ marginTop: '16px' }}>
            <Col span={24}>
              <Card 
                title={
                  <Space>
                    <UserOutlined />
                    {t('Student Details')}
                  </Space>
                }
                style={{ borderRadius: '12px' }}
              >
                <Row gutter={[12, 12]}>
                  <Col xs={24} sm={12} md={8}>
                    <div style={{ padding: '12px', background: '#f5f5f5', borderRadius: '8px' }}>
                      <Space align="center">
                        <MailOutlined style={{ color: '#1890ff', fontSize: '18px' }} />
                        <div>
                          <Text type="secondary" style={{ display: 'block', fontSize: '11px' }}>{t('email')}</Text>
                          <Text strong style={{ fontSize: '13px' }}>{student?.email}</Text>
                        </div>
                      </Space>
                    </div>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <div style={{ padding: '12px', background: '#f5f5f5', borderRadius: '8px' }}>
                      <Space align="center">
                        <PhoneOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
                        <div>
                          <Text type="secondary" style={{ display: 'block', fontSize: '11px' }}>{t('phone')}</Text>
                          <Text strong style={{ fontSize: '13px' }}>{student?.phone}</Text>
                        </div>
                      </Space>
                    </div>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <div style={{ padding: '12px', background: '#f5f5f5', borderRadius: '8px' }}>
                      <Space align="center">
                        <CalendarOutlined style={{ color: '#eb2f96', fontSize: '18px' }} />
                        <div>
                          <Text type="secondary" style={{ display: 'block', fontSize: '11px' }}>{t('dob')}</Text>
                          <Text strong style={{ fontSize: '13px' }}>
                            {student?.date_of_birth && formatDate(student.date_of_birth)}
                          </Text>
                        </div>
                      </Space>
                    </div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <div style={{ padding: '12px', background: '#f5f5f5', borderRadius: '8px' }}>
                      <Space align="center">
                        <HomeOutlined style={{ color: '#faad14', fontSize: '18px' }} />
                        <div>
                          <Text type="secondary" style={{ display: 'block', fontSize: '11px' }}>{t('address')}</Text>
                          <Text strong style={{ fontSize: '13px' }}>{student?.address}</Text>
                        </div>
                      </Space>
                    </div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <div style={{ padding: '12px', background: '#f5f5f5', borderRadius: '8px' }}>
                      <Space align="center">
                        <BookOutlined style={{ color: '#722ed1', fontSize: '18px' }} />
                        <div>
                          <Text type="secondary" style={{ display: 'block', fontSize: '11px' }}>{t('currentEducationLevel')}</Text>
                          <Text strong style={{ fontSize: '13px' }}>{student?.currenteducationlevel}</Text>
                        </div>
                      </Space>
                    </div>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>

          <Modal
            title={t('scheduleDetails')}
            open={!!popupData}
            onCancel={closePopup}
            footer={null}
            width={800}
          >
            <Table
              dataSource={popupData}
              pagination={false}
              columns={[
                {
                  title: t('day'),
                  dataIndex: 'date',
                  render: date => getDayOfWeek(date)
                },
                {
                  title: t('date'),
                  dataIndex: 'date',
                  render: date => formatDate(date)
                },
                {
                  title: t('time'),
                  render: (_, record) => 
                    `${formatTime(record.time)} - ${calculateEndTime(record.time, record.hours)}`
                },
                {
                  title: t('subjectName'),
                  dataIndex: 'subject'
                },
                {
                  title: t('teacherName'),
                  dataIndex: 'teacher'
                },
                {
                  title: t('hours'),
                  dataIndex: 'hours',
                  render: hours => `${hours} ${t('hr')}`
                }
              ]}
            />
          </Modal>
        </Content>
      </Layout>
    </Layout>
  );
}

export default withRoleAccess(StudentDashboard, ['student']);